import useEventListener from "@use-it/event-listener";
import { FloatingTooltip } from "../Tooltip/FloatingTooltip";
import type { ReactNode } from "react";
import { useRef } from "react";
import React from "react";

interface IProps {
  cancelText?: string;
  backText?: string;
  nextText?: string;
  cancelCb?: () => void;
  backCb?: () => void;
  backDisabled?: boolean;
  nextCb?: () => void;
  nextDisabled?: boolean;
  nextDisabledTooltipText?: string;
  customButton?: ReactNode;
  customButtonIndex?: number;
  nextDataTour?: string;
}

export default function FooterButtons({
  cancelText = "",
  cancelCb,
  backText = "",
  backCb,
  backDisabled = false,
  nextText = "",
  nextCb,
  nextDisabled = false,
  nextDisabledTooltipText = "",
  customButton = null,
  customButtonIndex = 0,
  nextDataTour,
}: IProps) {
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  useEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter" && nextButtonRef.current && !nextDisabled) {
      const ownDialog = nextButtonRef.current.closest("[role=dialog][aria-modal=true]");
      const dialogs = document.querySelectorAll("[role=dialog][aria-modal=true]");
      const topDialog = dialogs.length ? dialogs[dialogs.length - 1] : null;
      if (ownDialog !== topDialog) {
        return;
      }

      e.preventDefault();

      if (nextCb) {
        nextCb();
      } else {
        nextButtonRef.current.click();
      }
    }
  });

  const buttons: ReactNode[] = [];

  if (cancelText && cancelCb) {
    buttons.push(
      <button
        className="font-medium text-base border-0 mr-3"
        type="button"
        onClick={() => cancelCb()}
      >
        {cancelText}
      </button>,
    );
  }
  if (backText) {
    buttons.push(
      <button
        className="btn-alternative-outline mr-3"
        type="button"
        onClick={() => (backCb ? backCb() : null)}
        disabled={backDisabled}
      >
        {backText}
      </button>,
    );
  }
  if (nextText) {
    buttons.push(
      <FloatingTooltip content={nextDisabled ? nextDisabledTooltipText : ""} wrap>
        <button
          ref={nextButtonRef}
          className="btn-primary-fill"
          type="submit"
          data-tour={nextDataTour}
          onClick={() => (nextCb ? nextCb() : null)}
          disabled={nextDisabled}
        >
          {nextText}
        </button>
      </FloatingTooltip>,
    );
  }

  if (customButton) {
    if (customButtonIndex === 0 || customButtonIndex) {
      buttons.splice(customButtonIndex, 0, customButton);
    } else {
      buttons.push(customButton);
    }
  }

  return (
    <div
      className="sticky bottom-0 bg-gray-100 w-full py-3 flex items-center justify-end px-5 border-t border-gray-300 h-[4.1875rem]"
      data-tour-keep-out="true"
    >
      {buttons.map((button, index) => (
        <React.Fragment key={index}>{button}</React.Fragment>
      ))}
    </div>
  );
}
